/**
 * The box every Teslyn Cloud table sits in (docs/specs/ui-layout-rules.md rule 5; decision 1026, 3A; MB's UI audit
 * notes, causes cloud-cards and cloud-wide-tables):
 * - a table wider than its box scrolls sideways inside it, never the page, with its first column (the line, flight or
 *   project) kept in view, at every size;
 * - its headings wrap onto two lines at most;
 * - under 900 px wide (phones, iPads in portrait) a table of more than four columns becomes a list of cards: each row
 *   a card, its key value first, each value beside its column's name, the row's buttons at the card's foot and its
 *   tick box at the top; a table that sorts gets a "Sort by" list above its cards.
 * The table itself stays as each page writes it: this box reads its headings (`tableCards.ts`) and the styles do the
 * rest. `cards={false}` keeps a matrix (rows compared across, as a chart's data) a table on a phone.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';
import { readTable, sortButtons, type SortableHead } from './tableCards.ts';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';

/** More columns than this become cards on a phone or a narrow iPad (decision 1026). */
export const CARD_COLUMNS_OVER = 4;

interface Reading {
  readonly columns: number;
  readonly sortable: readonly SortableHead[];
}

const same = (a: Reading, b: Reading): boolean => JSON.stringify(a) === JSON.stringify(b);

export function TableWrap({
  children,
  cards = true,
  className,
}: {
  readonly children: ReactNode;
  /** False for a matrix (a chart's data, figures compared across rows): it stays a table, scrolling on a phone. */
  readonly cards?: boolean | undefined;
  readonly className?: string | undefined;
}): ReactElement {
  const wrap = useRef<HTMLDivElement>(null);
  const [reading, setReading] = useState<Reading>({ columns: 0, sortable: [] });
  const [scrolls, setScrolls] = useState(false);

  const read = useCallback(() => {
    const table = wrap.current?.querySelector('table');
    if (!table) return;
    const next = readTable(table);
    setReading((prev) => (same(prev, next) ? prev : next));
  }, []);

  // read the headings before the first paint, and again whenever the table's rows, words or order change
  useLayoutEffect(() => {
    read();
    const el = wrap.current;
    if (!el || typeof MutationObserver === 'undefined') return;
    const watch = new MutationObserver(read);
    watch.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-sort', 'colspan', 'aria-label'],
    });
    return () => watch.disconnect();
  }, [read]);

  // a table wider than its box: its first column stays in view (and shows its edge) while the rest scrolls
  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = (): void => setScrolls(el.scrollWidth > el.clientWidth + 1);
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    const table = el.querySelector('table');
    if (table) watch.observe(table);
    measure();
    return () => watch.disconnect();
  }, []);

  const asCards = cards && reading.columns > CARD_COLUMNS_OVER;
  return (
    <>
      {asCards && reading.sortable.length > 0 && <SortBy wrap={wrap} heads={reading.sortable} />}
      <div
        ref={wrap}
        className={['table-wrap', asCards && 'table-wrap--cards', scrolls && 'table-wrap--scrolls', className]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </>
  );
}

/**
 * The "Sort by" list above a sortable table's cards (its headings are hidden there): choosing a column sorts by it,
 * as a click on its heading does, and the order button turns the order round. Shown under 900 px only.
 */
function SortBy({
  wrap,
  heads,
}: {
  readonly wrap: RefObject<HTMLDivElement | null>;
  readonly heads: readonly SortableHead[];
}): ReactElement {
  const id = useId();
  const on = heads.find((h) => h.active);
  const click = (index: number): void => {
    const table = wrap.current?.querySelector('table');
    if (table) sortButtons(table)[index]?.click();
  };
  const dir = on?.dir ?? 'asc';
  return (
    <div className="table-sortby">
      <div className="field">
        <label className="label muted" htmlFor={id}>
          Sort by
        </label>
        <select
          id={id}
          className="input"
          value={on ? String(on.index) : ''}
          onChange={(e) => click(Number(e.target.value))}
        >
          {!on && <option value="">Choose a column</option>}
          {heads.map((h) => (
            <option key={h.index} value={String(h.index)}>
              {h.label}
            </option>
          ))}
        </select>
      </div>
      {on && (
        <Button
          variant="secondary"
          className="table-sortby__order"
          aria-label={`Order: ${dir === 'asc' ? 'ascending' : 'descending'}. Turn the order round`}
          onClick={() => click(on.index)}
        >
          <Icon name={dir === 'asc' ? 'arrow-up' : 'arrow-down'} />
          {dir === 'asc' ? 'Ascending' : 'Descending'}
        </Button>
      )}
    </div>
  );
}
